import {
  Box,
  TextField,
  CircularProgress,
  FormHelperText,
  Autocomplete,
} from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import { Controller, FieldValues, UseFormSetValue } from 'react-hook-form';
import { GenericObject } from 'types';
import { TeamLite, Team } from 'types/properties';
import { HookFormComponentProps } from 'ui-component/HookFormComponents/types';
import {
  useGetTeamsQuery,
  useLazyGetTeamQuery,
} from 'store/slices/apiV1/properties';
import ContextualCreateButton from 'ui-component/ContextualCreateButton';
import _ from 'lodash';
import CreateTeamDialog from 'views/properties/Teams/components/CreateTeamDialog';
import { ALL_APPS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import { Org } from 'types/org';

type HookFormTeamAutocompletePropsDisableRequired<
  T extends GenericObject | string
> = {
  disableRequiredProps: true;
  disabled?: boolean;
  mb?: number;
  fullWidth?: boolean;
  multiple?: boolean;
  size?: 'small' | 'medium' | never;
  setValue?: UseFormSetValue<FieldValues>;
  defaultValue?: T | T[];
  sx?: SxProps<Theme>;
  boxSx?: SxProps<Theme>;
  isInlineEdit?: boolean;
  overrideOrgId?: Org['id'];
  hideContextualCreate?: boolean;
};

export type BaseHookFormTeamAutocompleteProps<
  T extends GenericObject | string
> = {
  disabled?: boolean;
  mb?: number;
  fullWidth?: boolean;
  multiple?: boolean;
  setValue?: UseFormSetValue<FieldValues>;
  defaultValue?: T | T[];
  sx?: SxProps<Theme>;
  boxSx?: SxProps<Theme>;
  isInlineEdit?: boolean;
  overrideOrgId?: Org['id'];
  hideContextualCreate?: boolean;
  // size is inherited from HookFormComponentProps, but TypeScript needs it explicitly
  // in union types to ensure type safety
  size?: 'small' | 'medium';
};

type HookFormTeamAutocompleteProps<T extends GenericObject | string> =
  HookFormComponentProps &
    (
      | HookFormTeamAutocompletePropsDisableRequired<T>
      | BaseHookFormTeamAutocompleteProps<T>
    );

export const HookFormTeamAutocomplete = <T extends GenericObject | string>({
  errors,
  control,
  name = '',
  label,
  defaultValue,
  disabled,
  sx,
  boxSx,
  fullWidth = true,
  size = 'medium',
  setValue,
  isInlineEdit,
  overrideOrgId,
  shouldUnregister = false,
  hideContextualCreate = false,
  multiple = false,
}: HookFormTeamAutocompleteProps<T> & { multiple?: boolean }) => {
  const [newTeamDialogOpen, setNewTeamDialogOpen] = useState(false);
  const [selected, setSelected] = useState<
    (Team | TeamLite)[] | Team | TeamLite | null
  >(multiple ? [] : null);
  const [contextuallyCreatedOption, setContextuallyCreatedOption] =
    useState<Team | null>(null);
  const {
    data: teams,
    isFetching: isFetchingTeams,
    isSuccess: isSuccessTeams,
  } = useGetTeamsQuery();
  const [getTeam, { data: team, isFetching: isFetchingTeam }] =
    useLazyGetTeamQuery();

  const handleGetTeam = async (teamId: Team['id']) => {
    const fetchedTeam = await getTeam(teamId).unwrap();
    if (multiple) {
      setSelected((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        fetchedTeam,
      ]);
    } else {
      setSelected(fetchedTeam);
    }
  };

  const options: (Team | TeamLite)[] = useMemo(() => {
    const combinedOptions = [
      ...(contextuallyCreatedOption ? [contextuallyCreatedOption] : []),
      ...(teams || []),
      ...[team],
    ].filter((option) => option !== null && option !== undefined);

    return Array.from(new Set(combinedOptions.map((option) => option?.id)))
      .map((id) => combinedOptions.find((option) => option?.id === id))
      .filter((option): option is Team | TeamLite => {
        if (!option || option.archived) return false;
        if (overrideOrgId) {
          const orgId =
            typeof option.org === 'string' ? option.org : option.org.id;
          return orgId === overrideOrgId;
        }
        return true;
      });
  }, [teams, team, contextuallyCreatedOption?.id, overrideOrgId]);

  useEffect(() => {
    if (defaultValue) {
      if (multiple) {
        const defaultTeams = Array.isArray(defaultValue)
          ? defaultValue.map((id) => _.find(teams, (t) => t.id === id))
          : [];
        const validTeams = defaultTeams.filter(Boolean) as (Team | TeamLite)[];
        setSelected(validTeams);
      } else {
        const defaultTeam = _.find(teams, (t) => t.id === defaultValue);
        if (!defaultTeam && typeof defaultValue === 'string') {
          handleGetTeam(defaultValue);
        }
        setSelected(defaultTeam || null);
      }
    }
  }, [defaultValue, isSuccessTeams]);

  const AutoCompleteInput = (
    <Controller
      name={name}
      control={control}
      shouldUnregister={shouldUnregister}
      render={({ field: { onChange, onBlur } }) => (
        <Autocomplete
          multiple={multiple}
          loading={isFetchingTeams || isFetchingTeam}
          fullWidth={fullWidth}
          sx={{ ...sx }}
          options={options}
          size={size}
          onChange={(__, data) => {
            if (multiple) {
              const newData = data as (Team | TeamLite)[];
              setSelected(newData);
              onChange(newData.map((teamOption) => teamOption?.id));
            } else {
              const newData = data as Team | TeamLite | null;
              if (newData?.id !== (selected as Team | TeamLite | null)?.id) {
                setSelected(newData);
              }
              onChange(newData?.id || null);
            }
          }}
          value={selected}
          onBlur={onBlur}
          isOptionEqualToValue={(o, v) => o?.id === v?.id}
          getOptionLabel={(option) => option?.name || ''}
          renderInput={(params) => (
            <Box>
              <TextField
                {...params}
                label={label}
                fullWidth
                size={size}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isFetchingTeam || isFetchingTeams ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
              {errors?.[name]?.message && (
                <FormHelperText error id={`${name}Error`}>
                  {String(
                    (errors?.[name] as { message?: string })?.message || ''
                  )}
                </FormHelperText>
              )}
            </Box>
          )}
          disabled={disabled}
        />
      )}
    />
  );

  return (
    <Box sx={{ ...boxSx }}>
      {disabled || hideContextualCreate ? (
        AutoCompleteInput
      ) : (
        <>
          <ContextualCreateButton
            onClick={() => setNewTeamDialogOpen(true)}
            permissionScope={{
              app: ALL_APPS.PROPERTIES.id,
              accessLevel: appPermissionAccessLevels.edit,
            }}
            size={size}
            sx={
              isInlineEdit
                ? {
                    borderRadius: '0px',
                    borderRight: 'none',
                    height:
                      size === 'small'
                        ? '32px'
                        : size === 'medium'
                        ? '40px'
                        : '42px',
                    p: '5px',
                    minWidth: '0',
                    position: 'relative',
                    left: '-1px',
                    '.MuiButton-startIcon': { p: 0, m: 0 },
                  }
                : undefined
            }
          >
            {AutoCompleteInput}
          </ContextualCreateButton>
          <CreateTeamDialog
            dialogOpen={newTeamDialogOpen}
            onClose={() => setNewTeamDialogOpen(false)}
            onCreateSuccess={(value) => {
              const newTeam = value as Team;
              setContextuallyCreatedOption(newTeam);
              if (multiple) {
                setSelected((prev) => [
                  ...(Array.isArray(prev) ? prev : []),
                  newTeam,
                ]);
                setValue &&
                  setValue(
                    name,
                    [
                      ...(Array.isArray(selected)
                        ? selected.map((t) => t.id)
                        : []),
                      newTeam.id,
                    ],
                    {
                      shouldDirty: true,
                    }
                  );
              } else {
                setSelected(newTeam);
                setValue &&
                  setValue(name, newTeam.id, {
                    shouldDirty: true,
                  });
              }
            }}
            overrideOrgId={overrideOrgId}
          />
        </>
      )}
    </Box>
  );
};
